// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { AuditCarrierTrackingProperty } from '@/audit/api/types';
import { AuditCarrierLinkValue } from '@/audit/components/audit-carrier-link-value';
import { AuditPropertyUpdateLabel } from '@/audit/components/audit-property-update-label';
import { AuditPropertyValue } from '@/audit/components/audit-property-value';
import { toAuditCarrierName } from '@/audit/utils/to-audit-carrier-name';
import { ArrowForwardRounded } from '@/constants/hardfin-standard-icons';
import { Stack, Typography } from '@mui/material';

interface Props {
  property: AuditCarrierTrackingProperty;
}

/**
 * Renders carrier tracking as its own fields — carrier and tracking number —
 * rather than one collapsed string, so each reads on its own line and the
 * tracking number can link out to the carrier.
 */
export function AuditCarrierTrackingLabel(props: Props) {
  const isSnapshot = props.property.oldValue === undefined;

  // A row whose value is absent on both sides states nothing, so it is dropped
  // rather than rendered as "None → None". Setting only a carrier, or only a
  // tracking number, is a normal edit and would otherwise emit an empty row.
  const oldCarrier = toAuditCarrierName(props.property.oldValue);
  const newCarrier = toAuditCarrierName(props.property.newValue);
  const oldTracking = props.property.oldValue?.trackingNumber ?? null;
  const newTracking = props.property.newValue?.trackingNumber ?? null;
  const showCarrier = oldCarrier !== null || newCarrier !== null;
  const showTracking = oldTracking !== null || newTracking !== null;

  if (!showCarrier && !showTracking) {
    return null;
  }

  return (
    <Stack>
      <Typography component="span" variant="caption" fontWeight="bold">
        {props.property.name}:
      </Typography>
      {showCarrier && (
        <Typography variant="caption" component="div" marginLeft={2}>
          {isSnapshot ? (
            <>
              Carrier:&nbsp;
              <AuditPropertyValue value={newCarrier} />
            </>
          ) : (
            <AuditPropertyUpdateLabel
              name="Carrier"
              oldValue={oldCarrier}
              newValue={newCarrier}
            />
          )}
        </Typography>
      )}
      {showTracking && (
        <Typography
          variant="caption"
          component="div"
          marginLeft={2}
          display="flex"
          alignItems="center"
          flexWrap="wrap"
        >
          Tracking number:&nbsp;
          {!isSnapshot && (
            <>
              <AuditCarrierLinkValue
                carrier={props.property.oldValue?.carrier}
                identifier={oldTracking}
              />
              <ArrowForwardRounded fontSize="inherit" sx={{ marginX: 0.5 }} />
            </>
          )}
          <AuditCarrierLinkValue
            carrier={props.property.newValue?.carrier}
            identifier={newTracking}
          />
        </Typography>
      )}
    </Stack>
  );
}
